import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AdminRoute from './components/AdminRoute';
import AdminLayout from './components/AdminLayout';

// ── Lazy load public pages ──
const Home = lazy(() => import('./pages/Home'));
const Shop = lazy(() => import('./pages/Shop'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Support = lazy(() => import('./pages/Support'));
const Profile = lazy(() => import('./pages/Profile'));
const TrackOrder = lazy(() => import('./pages/TrackOrder'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));

// ── Lazy load admin pages ──
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminAnnouncements = lazy(() => import('./pages/admin/AdminAnnouncements'));
const AdminMeta = lazy(() => import('./pages/admin/AdminMeta'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));

const LoadingFallback = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div className="spinner" />
    </div>
);

function App() {
    return (
        <AuthProvider>
            <CartProvider>
                <Router>
                    <Toaster
                        position="top-center"
                        toastOptions={{
                            style: {
                                fontFamily: 'Montserrat, sans-serif',
                                fontSize: '0.85rem',
                                color: '#630000',
                                border: '1px solid #f2bdcd',
                                borderRadius: '8px',
                            },
                        }}
                    />

                    <Suspense fallback={<LoadingFallback />}>
                        <Routes>
                            {/* ── ADMIN ROUTES ── */}
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                                <Route index element={<AdminDashboard />} />
                                <Route path="products" element={<AdminProducts />} />
                                <Route path="orders" element={<AdminOrders />} />
                                <Route path="announcements" element={<AdminAnnouncements />} />
                                <Route path="meta" element={<AdminMeta />} />
                                <Route path="settings" element={<AdminSettings />} />
                            </Route>

                            {/* ── PUBLIC ROUTES ── */}
                            <Route path="/*" element={
                                <>
                                    <Navbar />
                                    <Routes>
                                        <Route path="/" element={<Home />} />
                                        <Route path="/shop" element={<Shop />} />
                                        {/*
                                          FIX: ProductCard.jsx, Cart.jsx, and CartDrawer.jsx all
                                          link to /product/:id — but only /shop/:id was ever
                                          registered here. Every "open product" link in the app
                                          was pointing at a route that didn't exist, silently
                                          rendering blank. Added /product/:id (the path everything
                                          actually uses) and kept /shop/:id for compatibility with
                                          anything else that might still use it.
                                        */}
                                        <Route path="/product/:id" element={<ProductDetail />} />
                                        <Route path="/shop/:id" element={<ProductDetail />} />
                                        <Route path="/cart" element={<Cart />} />
                                        <Route path="/checkout" element={<Checkout />} />
                                        <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
                                        <Route path="/track-order" element={<TrackOrder />} />
                                        <Route path="/about" element={<About />} />
                                        <Route path="/contact" element={<Contact />} />
                                        <Route path="/support" element={<Support />} />
                                        <Route path="/profile" element={<Profile />} />
                                        <Route path="/privacy" element={<PrivacyPolicy />} />
                                        <Route path="/terms" element={<Terms />} />
                                    </Routes>
                                    <Footer />
                                </>
                            } />
                        </Routes>
                    </Suspense>
                </Router>
            </CartProvider>
        </AuthProvider>
    );
}

export default App;